import { isLeverId, leversFromLegacy, type Action, type Feature, type LeverId, type RolePermissions, type SensitiveSwitch } from "@helmr/contracts";
import type { BranchRef } from "./auth";
import type { ApiClient } from "./client";

/** `GET /me` — **assumed: followups-tasks-api** (brief G1). Reconcile with the merged route. */
export interface Me {
  personId: string;
  staff: { id: string; name: string; roleId: string; roleName: string; isOwner: boolean };
  business: { id: string; name: string; timezone: string; currency: string; locale: string; country: string };
  branch: BranchRef;
  branches: BranchRef[];
  /** Legacy grants: kept until slice 9 for the `can` / `sees` shim only. */
  grants: RolePermissions | { owner: true };
  /**
   * D57: the caller's effective levers; "all" for the owner, who holds every lever implicitly. The server
   * always sends it. Optional here only so a fixture that predates levers still reads through `grants`.
   */
  levers?: LeverId[] | "all";
  /** The server's registry version (D57), so a client can tell its bundled registry is out of date. */
  leverRegistryVersion?: number;
}

export const getMe = (c: ApiClient) => c.get<Me>("/me");

const isOwnerGrant = (g: Me["grants"]): g is { owner: true } => "owner" in g && g.owner === true;

/**
 * Does the caller hold this lever? The owner holds every lever. Without `levers` (a fixture written before
 * slice 2a) the set is derived from the legacy grants with the same `leversFromLegacy` the server's
 * read-through uses, so the answer matches what the server would decide. Unknown ids are never held.
 */
export function hasLever(me: Me | null | undefined, lever: LeverId): boolean {
  if (!me || !isLeverId(lever)) return false;
  if (me.levers === "all" || isOwnerGrant(me.grants)) return true;
  let held = heldCache.get(me);
  if (!held) heldCache.set(me, (held = new Set(me.levers ?? leversFromLegacy(me.grants))));
  return held.has(lever);
}

const heldCache = new WeakMap<Me, ReadonlySet<LeverId>>();

/** @deprecated D57 shim until slice 9: use `hasLever` / `ctx.has`. */
export function canDo(me: Me | null | undefined, feature: Feature, action: Action): boolean {
  if (!me) return false;
  if (isOwnerGrant(me.grants)) return true;
  return me.grants.features[feature]?.includes(action) ?? false;
}

/** @deprecated D57 shim until slice 9: use `hasLever` / `ctx.has`. */
export function seesSwitch(me: Me | null | undefined, sw: SensitiveSwitch): boolean {
  if (!me) return false;
  if (isOwnerGrant(me.grants)) return true;
  return me.grants.sensitive.includes(sw);
}

/** What the caller holds, as a lever set: `"all"` for the owner. Falls back to the legacy grants on a server without `levers`. */
export function heldLevers(me: Me | null | undefined): ReadonlySet<LeverId> | "all" {
  if (!me) return new Set();
  if (me.levers === "all" || isOwnerGrant(me.grants)) return "all";
  return new Set(me.levers ?? leversFromLegacy(me.grants));
}
